import { z } from 'zod'
import {
  DOCUMENT_VERSION,
  MAX_ITEMS_PER_COLUMN,
  MAX_PAGES_PER_SITE,
  MAX_ROWS_PER_PAGE,
  THEME_FONT_PAIRS,
  THEME_PALETTES
} from '../config'
import {
  alignSchema,
  assetRefSchema,
  elementSchema,
  idSchema,
  isFullWidthElement
} from './elements'
import { languageCodeSchema, localizedTextSchema } from './localized'
import { siteSettingsSchema } from './site'

/**
 * The document a restaurant's website is. This file holds the shape as it is
 * stored; `publish.ts` adds the stricter rules that only apply when the owner
 * presses Publish.
 */

/*
  A colour is never a value in the document, only a role in the chosen palette.
  This is what makes §2 of the brief enforceable rather than a guideline: there
  is nowhere in the schema to put a hex.
*/
export const paletteSlotSchema = z.enum(['surface', 'muted', 'accent'])

/** `#rgb` or `#rrggbb`. The only place a colour is ever typed in this product. */
export const hexColorSchema = z
  .string()
  .regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'expected a hex colour')

/** Google spells every family it serves in letters, digits and spaces. */
export const fontFamilySchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9 ]+$/, 'expected a Google Fonts family name')

export const themeSchema = z.object({
  paletteId: z.enum(THEME_PALETTES),
  /*
    The venue's own colour. When it is set, the whole page palette is derived
    from it (render/palette.ts) and `paletteId` is ignored. Optional and
    additive, so every document written before this existed still parses and
    keeps the preset it had.

    A row still cannot hold a colour: it holds a role, and the roles are filled
    from here. The freedom moved up a level rather than being handed out.
  */
  brandColor: hexColorSchema.optional(),
  /*
    Individual colours, set in the builder. Each one overrides the value the
    brand colour would have produced for that role and nothing else.

    Kept apart from `brandColor` on purpose: the simple form owns the brand
    colour, the builder owns these, and while both are being edited neither
    writes the other's field. Building a page FROM the template is the one
    moment they meet, and there the template wins outright and drops these,
    because a page made from the template has to be the template's page.
    Optional and additive, so documents written before it parse unchanged.
  */
  colors: z
    .object({
      bg: hexColorSchema,
      surface: hexColorSchema,
      muted: hexColorSchema,
      accent: hexColorSchema,
      text: hexColorSchema,
      textMuted: hexColorSchema,
      primary: hexColorSchema,
      onPrimary: hexColorSchema,
      border: hexColorSchema
    })
    .partial()
    .optional(),
  fontPairId: z.enum(THEME_FONT_PAIRS),
  /*
    Families chosen one at a time, which beat the pair for whichever of the two
    they set. The pair stays the starting point and the fallback; this is the
    freedom on top of it, and it is the same arrangement as `brandColor` with
    `colors`. Optional and additive, so documents written before it parse.

    A name, not an id: Google's own spelling of the family, which is what the
    CSS API takes. Validated by shape only, because the catalogue of 1315
    families is data that changes without the schema changing, and a page whose
    font left the catalogue should still open.
  */
  fonts: z
    .object({
      heading: fontFamilySchema.optional(),
      body: fontFamilySchema.optional()
    })
    .optional(),
  radius: z.enum(['none', 'S', 'M', 'L']),
  buttonStyle: z.enum(['solid', 'outline', 'pill'])
})

export const rowBackgroundSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('none') }),
  z.object({ type: z.literal('color'), slot: paletteSlotSchema }),
  z.object({
    type: z.literal('image'),
    assetId: assetRefSchema,
    /*
      Text over a photo is the single most reliable way to make a page
      unreadable, so a background image always carries a scrim and the only
      choice is how strong it is. There is no "none".
    */
    overlay: z.enum(['light', 'medium', 'strong'])
  })
])

export const rowLayoutSchema = z.enum([
  '1',
  '1-1',
  '1-1-1',
  '1-1-1-1',
  '1-2',
  '2-1'
])

export type RowLayout = z.infer<typeof rowLayoutSchema>

/** How many columns each layout has, and the fractions they take. */
export const LAYOUT_COLUMNS: Record<RowLayout, number[]> = {
  '1': [1],
  '1-1': [1, 1],
  '1-1-1': [1, 1, 1],
  '1-1-1-1': [1, 1, 1, 1],
  '1-2': [1, 2],
  '2-1': [2, 1]
}

export const columnSchema = z.object({
  id: idSchema,
  align: alignSchema,
  items: z.array(elementSchema).max(MAX_ITEMS_PER_COLUMN)
})

const rowShape = z.object({
  id: idSchema,
  layout: rowLayoutSchema,
  background: rowBackgroundSchema,
  paddingY: z.enum(['S', 'M', 'L']),
  maxWidth: z.enum(['contained', 'full']),
  verticalAlign: z.enum(['top', 'center', 'bottom']),
  gap: z.enum(['S', 'M', 'L']),
  /** `keep2` is for photo grids, which look wrong stacked one per screen. */
  mobileBehavior: z.enum(['stack', 'keep2']),
  /** Hidden keeps a row in the document without rendering it. */
  hidden: z.boolean(),
  columns: z.array(columnSchema).min(1).max(4)
})

export const rowSchema = rowShape.superRefine((row, ctx) => {
  const expected = LAYOUT_COLUMNS[row.layout].length
  if (row.columns.length !== expected) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['columns'],
      message: `layout "${row.layout}" needs ${expected} columns, got ${row.columns.length}`
    })
  }

  /*
    The composition rule from §4 of the brief. A form, a reservation widget, a
    menu, a video and a map each own the full width of a row: dropped into a
    quarter column they are unusable on a phone and unreadable on a desktop.

    Allegro solves the same problem by silently "repairing" the description.
    We refuse the document instead and say which element caused it, because a
    silent repair means an owner's page changes without them being told.
  */
  row.columns.forEach((column, columnIndex) => {
    column.items.forEach((item, itemIndex) => {
      if (!isFullWidthElement(item)) return

      const path = ['columns', columnIndex, 'items', itemIndex] as const
      if (row.layout !== '1') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...path],
          message: `"${item.type}" may only be used in a single-column row`
        })
      }
      if (column.items.length > 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...path],
          message: `"${item.type}" has to be the only element in its column`
        })
      }
    })
  })
})

export type Row = z.infer<typeof rowSchema>

/*
  The home page is the empty slug. Everything else is lowercase, unaccented and
  hyphenated, because it becomes a path segment under
  `mojadomena.pl/{miasto}/{lokal}/{slug}` and a URL that needs percent-encoding
  is a URL nobody can read out over the phone.
*/
export const pageSlugSchema = z.union([
  z.literal(''),
  z
    .string()
    .max(80)
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      'use lowercase letters, digits and hyphens'
    )
])

export const seoSchema = z.object({
  title: localizedTextSchema,
  description: localizedTextSchema,
  ogImageAssetId: assetRefSchema.optional()
})

export const pageSchema = z.object({
  id: idSchema,
  slug: pageSlugSchema,
  title: localizedTextSchema,
  seo: seoSchema,
  status: z.enum(['draft', 'published']),
  blocks: z.array(rowSchema).max(MAX_ROWS_PER_PAGE)
})

export type Page = z.infer<typeof pageSchema>

export const documentSchema = z
  .object({
    version: z.number().int().positive(),
    /*
      Which template produced this document, and the settings it was produced
      from. Both optional and both additive, so every document written before
      templates existed still parses and needs no migration: the settings form
      simply has nothing to open, and the builder works exactly as it did.
    */
    templateId: z.string().max(64).optional(),
    // `venue`, not `site`: the key every saved draft and published page holds.
    venue: siteSettingsSchema.optional(),
    /*
      The site's languages, mirroring what the venue has enabled for its menu
      (`GET /restaurants/{id}/languages`). Kept in the document rather than read
      live, so a published page does not change shape when someone edits the
      menu's language list.
    */
    languages: z.array(languageCodeSchema).min(1).max(10),
    defaultLanguage: languageCodeSchema,
    theme: themeSchema,
    pages: z.array(pageSchema).min(1).max(MAX_PAGES_PER_SITE)
  })
  .superRefine((doc, ctx) => {
    if (!doc.languages.includes(doc.defaultLanguage)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['defaultLanguage'],
        message: 'the default language has to be one of the site languages'
      })
    }

    if (new Set(doc.languages).size !== doc.languages.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['languages'],
        message: 'a language cannot be listed twice'
      })
    }

    /*
      Slugs decide URLs, so two pages sharing one is not a cosmetic problem: the
      prerenderer would write both to the same path and the second would win,
      silently replacing a page the owner still sees in the editor.
    */
    const slugs = new Set<string>()
    doc.pages.forEach((page, index) => {
      if (slugs.has(page.slug)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pages', index, 'slug'],
          message: 'two pages cannot share a slug'
        })
      }
      slugs.add(page.slug)
    })

    const ids = new Set<string>()
    doc.pages.forEach((page, index) => {
      if (ids.has(page.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pages', index, 'id'],
          message: 'two pages cannot share an id'
        })
      }
      ids.add(page.id)
    })
  })

export type WebsiteDocument = z.infer<typeof documentSchema>

/** Whether a parsed document is the version this build writes. */
export function isCurrentVersion(doc: { version: number }): boolean {
  return doc.version === DOCUMENT_VERSION
}
